import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import TeamMemberEditor, { type TeamMember } from './components/TeamMemberEditor';
import PricingEditor from './components/PricingEditor';
import { teamMembers } from '@/mocks/teamMembers';

interface EditableField {
  key: string;
  label: string;
  labelKey: string;
  type: 'text' | 'textarea' | 'image';
  currentValue: string;
}

const SUPPORTED_LANGS = [
  { code: 'de', label: 'DE', flag: '🇩🇪' },
  { code: 'en', label: 'EN', flag: '🇬🇧' },
  { code: 'it', label: 'IT', flag: '🇮🇹' },
  { code: 'es', label: 'ES', flag: '🇪🇸' },
  { code: 'fr', label: 'FR', flag: '🇫🇷' },
];

// ── Extended PAGE_FIELDS for all pages ─────────────────────────────────────
const PAGE_FIELDS: Record<string, EditableField[]> = {
  home: [
    { key: 'hero.title_line1', label: 'Hero — Zeile 1', labelKey: 'admy.f_hero_line1', type: 'text', currentValue: '' },
    { key: 'hero.title_line2', label: 'Hero — Zeile 2', labelKey: 'admy.f_hero_line2', type: 'text', currentValue: '' },
    { key: 'hero.subtitle', label: 'Hero — Untertitel', labelKey: 'admy.f_hero_subtitle', type: 'textarea', currentValue: '' },
    { key: 'hero.cta', label: 'Hero — CTA Button', labelKey: 'admy.f_hero_cta', type: 'text', currentValue: '' },
    { key: 'services.tag', label: 'Services — Tag', labelKey: 'admy.f_services_tag', type: 'text', currentValue: '' },
    { key: 'services.heading', label: 'Services — Überschrift', labelKey: 'admy.f_services_heading', type: 'text', currentValue: '' },
    { key: 'services.subtitle', label: 'Services — Untertitel', labelKey: 'admy.f_services_subtitle', type: 'textarea', currentValue: '' },
    { key: 'services.hair_transplant.title', label: 'Haartransplantation — Titel', labelKey: 'admy.f_hair_title', type: 'text', currentValue: '' },
    { key: 'services.hair_transplant.desc', label: 'Haartransplantation — Beschreibung', labelKey: 'admy.f_hair_desc', type: 'textarea', currentValue: '' },
    { key: 'services.dental.title', label: 'Dental-Kosmetik — Titel', labelKey: 'admy.f_dental_title', type: 'text', currentValue: '' },
    { key: 'services.dental.desc', label: 'Dental-Kosmetik — Beschreibung', labelKey: 'admy.f_dental_desc', type: 'textarea', currentValue: '' },
    { key: 'services.botox.title', label: 'Botox & Filler — Titel', labelKey: 'admy.f_botox_title', type: 'text', currentValue: '' },
    { key: 'services.botox.desc', label: 'Botox & Filler — Beschreibung', labelKey: 'admy.f_botox_desc', type: 'textarea', currentValue: '' },
    { key: 'services.cta', label: 'Services — CTA Text', labelKey: 'admy.f_services_cta', type: 'text', currentValue: '' },
    { key: 'about.tag', label: 'About — Tag', labelKey: 'admy.f_about_tag', type: 'text', currentValue: '' },
    { key: 'about.heading_line1', label: 'About — Zeile 1', labelKey: 'admy.f_about_line1', type: 'text', currentValue: '' },
    { key: 'about.heading_line2', label: 'About — Zeile 2', labelKey: 'admy.f_about_line2', type: 'text', currentValue: '' },
    { key: 'about.description', label: 'About — Beschreibung', labelKey: 'admy.f_about_desc', type: 'textarea', currentValue: '' },
    { key: 'about.cta', label: 'About — CTA', labelKey: 'admy.f_about_cta', type: 'text', currentValue: '' },
    { key: 'pricing.tag', label: 'Preise — Tag', labelKey: 'admy.f_pricing_tag', type: 'text', currentValue: '' },
    { key: 'pricing.heading_line1', label: 'Preise — Zeile 1', labelKey: 'admy.f_pricing_line1', type: 'text', currentValue: '' },
    { key: 'pricing.heading_line2', label: 'Preise — Zeile 2', labelKey: 'admy.f_pricing_line2', type: 'text', currentValue: '' },
    { key: 'pricing.subtitle', label: 'Preise — Untertitel', labelKey: 'admy.f_pricing_subtitle', type: 'textarea', currentValue: '' },
    { key: 'pricing.cta', label: 'Preise — CTA', labelKey: 'admy.f_pricing_cta', type: 'text', currentValue: '' },
    { key: 'testimonials.tag', label: 'Bewertungen — Tag', labelKey: 'admy.f_testimonials_tag', type: 'text', currentValue: '' },
    { key: 'testimonials.heading', label: 'Bewertungen — Überschrift', labelKey: 'admy.f_testimonials_heading', type: 'text', currentValue: '' },
    { key: 'testimonials.subtitle', label: 'Bewertungen — Untertitel', labelKey: 'admy.f_testimonials_subtitle', type: 'textarea', currentValue: '' },
    { key: 'testimonial1.text', label: 'Bewertung 1 — Text', labelKey: 'admy.f_testimonial1_text', type: 'textarea', currentValue: '' },
    { key: 'testimonial1.name', label: 'Bewertung 1 — Name', labelKey: 'admy.f_testimonial1_name', type: 'text', currentValue: '' },
    { key: 'testimonial1.role', label: 'Bewertung 1 — Rolle', labelKey: 'admy.f_testimonial1_role', type: 'text', currentValue: '' },
    { key: 'testimonial2.text', label: 'Bewertung 2 — Text', labelKey: 'admy.f_testimonial2_text', type: 'textarea', currentValue: '' },
    { key: 'testimonial2.name', label: 'Bewertung 2 — Name', labelKey: 'admy.f_testimonial2_name', type: 'text', currentValue: '' },
    { key: 'testimonial2.role', label: 'Bewertung 2 — Rolle', labelKey: 'admy.f_testimonial2_role', type: 'text', currentValue: '' },
    { key: 'testimonial3.text', label: 'Bewertung 3 — Text', labelKey: 'admy.f_testimonial3_text', type: 'textarea', currentValue: '' },
    { key: 'testimonial3.name', label: 'Bewertung 3 — Name', labelKey: 'admy.f_testimonial3_name', type: 'text', currentValue: '' },
    { key: 'testimonial3.role', label: 'Bewertung 3 — Rolle', labelKey: 'admy.f_testimonial3_role', type: 'text', currentValue: '' },
    { key: 'testimonial4.text', label: 'Bewertung 4 — Text', labelKey: 'admy.f_testimonial4_text', type: 'textarea', currentValue: '' },
    { key: 'testimonial4.name', label: 'Bewertung 4 — Name', labelKey: 'admy.f_testimonial4_name', type: 'text', currentValue: '' },
    { key: 'testimonial4.role', label: 'Bewertung 4 — Rolle', labelKey: 'admy.f_testimonial4_role', type: 'text', currentValue: '' },
    { key: 'testimonial5.text', label: 'Bewertung 5 — Text', labelKey: 'admy.f_testimonial5_text', type: 'textarea', currentValue: '' },
    { key: 'testimonial5.name', label: 'Bewertung 5 — Name', labelKey: 'admy.f_testimonial5_name', type: 'text', currentValue: '' },
    { key: 'testimonial5.role', label: 'Bewertung 5 — Rolle', labelKey: 'admy.f_testimonial5_role', type: 'text', currentValue: '' },
    { key: 'booking.tag', label: 'Booking — Tag', labelKey: 'admy.f_booking_tag', type: 'text', currentValue: '' },
    { key: 'booking.heading_line1', label: 'Booking — Zeile 1', labelKey: 'admy.f_booking_line1', type: 'text', currentValue: '' },
    { key: 'booking.heading_line2', label: 'Booking — Zeile 2', labelKey: 'admy.f_booking_line2', type: 'text', currentValue: '' },
    { key: 'booking.description', label: 'Booking — Beschreibung', labelKey: 'admy.f_booking_desc', type: 'textarea', currentValue: '' },
    { key: 'booking.cta', label: 'Booking — CTA', labelKey: 'admy.f_booking_cta', type: 'text', currentValue: '' },
    { key: 'process.tag', label: 'Prozess — Tag', labelKey: 'admy.f_process_tag', type: 'text', currentValue: '' },
    { key: 'process.heading', label: 'Prozess — Überschrift', labelKey: 'admy.f_process_heading', type: 'text', currentValue: '' },
    { key: 'process.subtitle', label: 'Prozess — Untertitel', labelKey: 'admy.f_process_subtitle', type: 'textarea', currentValue: '' },
    { key: 'trust.tag', label: 'Vertrauen — Tag', labelKey: 'admy.f_trust_tag', type: 'text', currentValue: '' },
    { key: 'trust.heading', label: 'Vertrauen — Überschrift', labelKey: 'admy.f_trust_heading', type: 'text', currentValue: '' },
    { key: 'trust.subtitle', label: 'Vertrauen — Untertitel', labelKey: 'admy.f_trust_subtitle', type: 'textarea', currentValue: '' },
    { key: 'instagram.heading', label: 'Instagram — Überschrift', labelKey: 'admy.f_instagram_heading', type: 'text', currentValue: '' },
    { key: 'instagram.cta', label: 'Instagram — CTA', labelKey: 'admy.f_instagram_cta', type: 'text', currentValue: '' },
    { key: 'footer.company', label: 'Footer — Firmenname', labelKey: 'admy.f_footer_company', type: 'text', currentValue: '' },
    { key: 'footer.address', label: 'Footer — Adresse', labelKey: 'admy.f_footer_address', type: 'textarea', currentValue: '' },
    { key: 'footer.phone', label: 'Footer — Telefon', labelKey: 'admy.f_footer_phone', type: 'text', currentValue: '' },
    { key: 'footer.email', label: 'Footer — E-Mail', labelKey: 'admy.f_footer_email', type: 'text', currentValue: '' },
    { key: 'images.home.hero_bg', label: 'Bild — Hero Hintergrund', labelKey: 'admy.f_img_hero_bg', type: 'image', currentValue: '' },
    { key: 'images.home.services_hair_img', label: 'Bild — Haartransplantation', labelKey: 'admy.f_img_hair', type: 'image', currentValue: '' },
    { key: 'images.home.services_dental_img', label: 'Bild — Dental-Kosmetik', labelKey: 'admy.f_img_dental', type: 'image', currentValue: '' },
    { key: 'images.home.services_botox_img', label: 'Bild — Botox & Filler', labelKey: 'admy.f_img_botox', type: 'image', currentValue: '' },
    { key: 'images.home.about_img', label: 'Bild — Über uns Team', labelKey: 'admy.f_img_about_team', type: 'image', currentValue: '' },
    { key: 'images.home.cta_bg', label: 'Bild — CTA Hintergrund', labelKey: 'admy.f_img_cta_bg', type: 'image', currentValue: '' },
    { key: 'images.home.process_img', label: 'Bild — Prozess Hintergrund', labelKey: 'admy.f_img_process_bg', type: 'image', currentValue: '' },
    { key: 'images.home.trust_img', label: 'Bild — Vertrauen Hintergrund', labelKey: 'admy.f_img_trust_bg', type: 'image', currentValue: '' },
    { key: 'images.home.instagram_1', label: 'Bild — Instagram 1', labelKey: 'admy.f_img_instagram_1', type: 'image', currentValue: '' },
    { key: 'images.home.instagram_2', label: 'Bild — Instagram 2', labelKey: 'admy.f_img_instagram_2', type: 'image', currentValue: '' },
    { key: 'images.home.instagram_3', label: 'Bild — Instagram 3', labelKey: 'admy.f_img_instagram_3', type: 'image', currentValue: '' },
    { key: 'images.home.instagram_4', label: 'Bild — Instagram 4', labelKey: 'admy.f_img_instagram_4', type: 'image', currentValue: '' },
  ],
  about: [
    { key: 'about.tag', label: 'Tag', labelKey: 'admy.f_tag', type: 'text', currentValue: '' },
    { key: 'about.heading_line1', label: 'Headline Zeile 1', labelKey: 'admy.f_headline_line1', type: 'text', currentValue: '' },
    { key: 'about.heading_line2', label: 'Headline Zeile 2', labelKey: 'admy.f_headline_line2', type: 'text', currentValue: '' },
    { key: 'about.description', label: 'Beschreibung', labelKey: 'admy.f_description', type: 'textarea', currentValue: '' },
    { key: 'about.quote', label: 'Zitat', labelKey: 'admy.f_quote', type: 'textarea', currentValue: '' },
    { key: 'about.name', label: 'Name', labelKey: 'admy.f_name', type: 'text', currentValue: '' },
    { key: 'about.position', label: 'Position', labelKey: 'admy.f_position', type: 'text', currentValue: '' },
    { key: 'about.mission_title', label: 'Mission — Titel', labelKey: 'admy.f_mission_title', type: 'text', currentValue: '' },
    { key: 'about.mission_text', label: 'Mission — Text', labelKey: 'admy.f_mission_text', type: 'textarea', currentValue: '' },
    { key: 'about.values_title', label: 'Werte — Titel', labelKey: 'admy.f_values_title', type: 'text', currentValue: '' },
    { key: 'about.values_text', label: 'Werte — Text', labelKey: 'admy.f_values_text', type: 'textarea', currentValue: '' },
    { key: 'about.why_albania_title', label: 'Warum Albanien — Titel', labelKey: 'admy.f_why_albania_title', type: 'text', currentValue: '' },
    { key: 'about.why_albania_text', label: 'Warum Albanien — Text', labelKey: 'admy.f_why_albania_text', type: 'textarea', currentValue: '' },
    { key: 'about.cta_heading', label: 'CTA — Überschrift', labelKey: 'admy.f_cta_heading', type: 'text', currentValue: '' },
    { key: 'about.cta_subtitle', label: 'CTA — Untertitel', labelKey: 'admy.f_cta_subtitle', type: 'textarea', currentValue: '' },
    { key: 'about.cta_button', label: 'CTA — Button', labelKey: 'admy.f_cta_button', type: 'text', currentValue: '' },
    { key: 'images.about.hero_bg', label: 'Bild — Hero Hintergrund (Tirana)', labelKey: 'admy.f_img_hero_bg_tirana', type: 'image', currentValue: '' },
    { key: 'images.about.doctor_img', label: 'Bild — Team/Arzt Foto', labelKey: 'admy.f_img_doctor', type: 'image', currentValue: '' },
    { key: 'images.about.mission_img', label: 'Bild — Mission', labelKey: 'admy.f_img_mission', type: 'image', currentValue: '' },
    { key: 'images.about.clinic_img', label: 'Bild — Klinik', labelKey: 'admy.f_img_clinic', type: 'image', currentValue: '' },
  ],
  services: [
    { key: 'services.tag', label: 'Tag', labelKey: 'admy.f_tag', type: 'text', currentValue: '' },
    { key: 'services.heading', label: 'Headline', labelKey: 'admy.f_headline', type: 'text', currentValue: '' },
    { key: 'services.subtitle', label: 'Untertitel', labelKey: 'admy.f_subtitle', type: 'textarea', currentValue: '' },
    { key: 'services.hair_transplant.title', label: 'Haartransplantation — Titel', labelKey: 'admy.f_hair_title', type: 'text', currentValue: '' },
    { key: 'services.hair_transplant.desc', label: 'Haartransplantation — Text', labelKey: 'admy.f_hair_text', type: 'textarea', currentValue: '' },
    { key: 'services.hair_transplant.price', label: 'Haartransplantation — Preis', labelKey: 'admy.f_hair_price', type: 'text', currentValue: '' },
    { key: 'services.dental.title', label: 'Dental — Titel', labelKey: 'admy.f_dental2_title', type: 'text', currentValue: '' },
    { key: 'services.dental.desc', label: 'Dental — Text', labelKey: 'admy.f_dental2_text', type: 'textarea', currentValue: '' },
    { key: 'services.dental.price', label: 'Dental — Preis', labelKey: 'admy.f_dental2_price', type: 'text', currentValue: '' },
    { key: 'services.botox.title', label: 'Botox — Titel', labelKey: 'admy.f_botox2_title', type: 'text', currentValue: '' },
    { key: 'services.botox.desc', label: 'Botox — Text', labelKey: 'admy.f_botox2_text', type: 'textarea', currentValue: '' },
    { key: 'services.botox.price', label: 'Botox — Preis', labelKey: 'admy.f_botox2_price', type: 'text', currentValue: '' },
    { key: 'services.surgery.title', label: 'Chirurgie — Titel', labelKey: 'admy.f_surgery_title', type: 'text', currentValue: '' },
    { key: 'services.surgery.desc', label: 'Chirurgie — Text', labelKey: 'admy.f_surgery_text', type: 'textarea', currentValue: '' },
    { key: 'services.surgery.price', label: 'Chirurgie — Preis', labelKey: 'admy.f_surgery_price', type: 'text', currentValue: '' },
    { key: 'services.cta', label: 'CTA Text', labelKey: 'admy.f_cta_text', type: 'text', currentValue: '' },
    { key: 'services.cta_subtitle', label: 'CTA Untertitel', labelKey: 'admy.f_cta_subtitle2', type: 'textarea', currentValue: '' },
    { key: 'images.services.hero_bg', label: 'Bild — Hero Hintergrund', labelKey: 'admy.f_img_hero_bg', type: 'image', currentValue: '' },
    { key: 'images.services.hair_img', label: 'Bild — Haartransplantation', labelKey: 'admy.f_img_hair', type: 'image', currentValue: '' },
    { key: 'images.services.dental_img', label: 'Bild — Dental', labelKey: 'admy.f_img_dental2', type: 'image', currentValue: '' },
    { key: 'images.services.botox_img', label: 'Bild — Botox', labelKey: 'admy.f_img_botox2', type: 'image', currentValue: '' },
    { key: 'images.services.surgery_img', label: 'Bild — Chirurgie', labelKey: 'admy.f_img_surgery', type: 'image', currentValue: '' },
  ],
  pricing: [
    { key: 'pricing.tag', label: 'Tag', labelKey: 'admy.f_tag', type: 'text', currentValue: '' },
    { key: 'pricing.heading_line1', label: 'Headline Zeile 1', labelKey: 'admy.f_headline_line1', type: 'text', currentValue: '' },
    { key: 'pricing.heading_line2', label: 'Headline Zeile 2', labelKey: 'admy.f_headline_line2', type: 'text', currentValue: '' },
    { key: 'pricing.subtitle', label: 'Untertitel', labelKey: 'admy.f_subtitle', type: 'textarea', currentValue: '' },
    { key: 'pricing.basic.title', label: 'Essential — Titel', labelKey: 'admy.f_essential_title', type: 'text', currentValue: '' },
    { key: 'pricing.basic.price', label: 'Essential — Preis', labelKey: 'admy.f_essential_price', type: 'text', currentValue: '' },
    { key: 'pricing.basic.desc', label: 'Essential — Beschreibung', labelKey: 'admy.f_essential_desc', type: 'textarea', currentValue: '' },
    { key: 'pricing.basic.features', label: 'Essential — Features (kommagetrennt)', labelKey: 'admy.f_essential_features', type: 'textarea', currentValue: '' },
    { key: 'pricing.premium.title', label: 'Premium — Titel', labelKey: 'admy.f_premium_title', type: 'text', currentValue: '' },
    { key: 'pricing.premium.price', label: 'Premium — Preis', labelKey: 'admy.f_premium_price', type: 'text', currentValue: '' },
    { key: 'pricing.premium.desc', label: 'Premium — Beschreibung', labelKey: 'admy.f_premium_desc', type: 'textarea', currentValue: '' },
    { key: 'pricing.premium.features', label: 'Premium — Features (kommagetrennt)', labelKey: 'admy.f_premium_features', type: 'textarea', currentValue: '' },
    { key: 'pricing.luxe.title', label: 'Luxe — Titel', labelKey: 'admy.f_luxe_title', type: 'text', currentValue: '' },
    { key: 'pricing.luxe.price', label: 'Luxe — Preis', labelKey: 'admy.f_luxe_price', type: 'text', currentValue: '' },
    { key: 'pricing.luxe.desc', label: 'Luxe — Beschreibung', labelKey: 'admy.f_luxe_desc', type: 'textarea', currentValue: '' },
    { key: 'pricing.luxe.features', label: 'Luxe — Features (kommagetrennt)', labelKey: 'admy.f_luxe_features', type: 'textarea', currentValue: '' },
    { key: 'pricing.exclusif.title', label: 'Exclusif — Titel', labelKey: 'admy.f_exclusif_title', type: 'text', currentValue: '' },
    { key: 'pricing.exclusif.price', label: 'Exclusif — Preis', labelKey: 'admy.f_exclusif_price', type: 'text', currentValue: '' },
    { key: 'pricing.exclusif.desc', label: 'Exclusif — Beschreibung', labelKey: 'admy.f_exclusif_desc', type: 'textarea', currentValue: '' },
    { key: 'pricing.exclusif.features', label: 'Exclusif — Features (kommagetrennt)', labelKey: 'admy.f_exclusif_features', type: 'textarea', currentValue: '' },
    { key: 'pricing.cta', label: 'CTA Text', labelKey: 'admy.f_cta_text', type: 'text', currentValue: '' },
    { key: 'pricing.guarantee', label: 'Garantie-Text', labelKey: 'admy.f_guarantee_text', type: 'textarea', currentValue: '' },
    { key: 'images.pricing.hero_bg', label: 'Bild — Hero Hintergrund', labelKey: 'admy.f_img_hero_bg', type: 'image', currentValue: '' },
    { key: 'images.pricing.essential_img', label: 'Bild — Essential Paket', labelKey: 'admy.f_img_essential', type: 'image', currentValue: '' },
    { key: 'images.pricing.premium_img', label: 'Bild — Premium Paket', labelKey: 'admy.f_img_premium', type: 'image', currentValue: '' },
    { key: 'images.pricing.luxe_img', label: 'Bild — Luxe Paket', labelKey: 'admy.f_img_luxe', type: 'image', currentValue: '' },
    { key: 'images.pricing.exclusif_img', label: 'Bild — Exclusif Paket', labelKey: 'admy.f_img_exclusif', type: 'image', currentValue: '' },
  ],
  booking: [
    { key: 'booking.tag', label: 'Tag', labelKey: 'admy.f_tag', type: 'text', currentValue: '' },
    { key: 'booking.heading_line1', label: 'Headline Zeile 1', labelKey: 'admy.f_headline_line1', type: 'text', currentValue: '' },
    { key: 'booking.heading_line2', label: 'Headline Zeile 2', labelKey: 'admy.f_headline_line2', type: 'text', currentValue: '' },
    { key: 'booking.description', label: 'Beschreibung', labelKey: 'admy.f_description', type: 'textarea', currentValue: '' },
    { key: 'booking.cta', label: 'CTA Text', labelKey: 'admy.f_cta_text', type: 'text', currentValue: '' },
    { key: 'booking.hero_subtitle', label: 'Hero Untertitel', labelKey: 'admy.f_hero_subtitle2', type: 'textarea', currentValue: '' },
    { key: 'booking.form_title', label: 'Formular — Titel', labelKey: 'admy.f_form_title', type: 'text', currentValue: '' },
    { key: 'booking.form_subtitle', label: 'Formular — Untertitel', labelKey: 'admy.f_form_subtitle', type: 'textarea', currentValue: '' },
    { key: 'booking.firstname', label: 'Formular — Vorname Label', labelKey: 'admy.f_form_firstname', type: 'text', currentValue: '' },
    { key: 'booking.lastname', label: 'Formular — Nachname Label', labelKey: 'admy.f_form_lastname', type: 'text', currentValue: '' },
    { key: 'booking.email', label: 'Formular — E-Mail Label', labelKey: 'admy.f_form_email', type: 'text', currentValue: '' },
    { key: 'booking.phone', label: 'Formular — Telefon Label', labelKey: 'admy.f_form_phone', type: 'text', currentValue: '' },
    { key: 'booking.service_label', label: 'Formular — Behandlung Label', labelKey: 'admy.f_form_service', type: 'text', currentValue: '' },
    { key: 'booking.message_label', label: 'Formular — Nachricht Label', labelKey: 'admy.f_form_message', type: 'text', currentValue: '' },
    { key: 'booking.privacy_text', label: 'Formular — Datenschutz Text', labelKey: 'admy.f_form_privacy', type: 'textarea', currentValue: '' },
    { key: 'booking.success_title', label: 'Erfolg — Titel', labelKey: 'admy.f_success_title', type: 'text', currentValue: '' },
    { key: 'booking.success_message', label: 'Erfolg — Nachricht', labelKey: 'admy.f_success_message', type: 'textarea', currentValue: '' },
    { key: 'booking.find_us', label: 'Kontakt — Tag', labelKey: 'admy.f_contact_tag', type: 'text', currentValue: '' },
    { key: 'booking.visit_us', label: 'Kontakt — Überschrift', labelKey: 'admy.f_contact_heading', type: 'text', currentValue: '' },
    { key: 'booking.visit_subtitle', label: 'Kontakt — Untertitel', labelKey: 'admy.f_contact_subtitle', type: 'textarea', currentValue: '' },
    { key: 'images.booking.hero_bg', label: 'Bild — Hero Hintergrund', labelKey: 'admy.f_img_hero_bg', type: 'image', currentValue: '' },
  ],
  team: [
    { key: 'team.tag', label: 'Tag', labelKey: 'admy.f_tag', type: 'text', currentValue: '' },
    { key: 'team.heading_line1', label: 'Headline Zeile 1', labelKey: 'admy.f_headline_line1', type: 'text', currentValue: '' },
    { key: 'team.heading_line2', label: 'Headline Zeile 2', labelKey: 'admy.f_headline_line2', type: 'text', currentValue: '' },
    { key: 'team.subtitle', label: 'Untertitel', labelKey: 'admy.f_subtitle', type: 'textarea', currentValue: '' },
    { key: 'team.cta_heading', label: 'CTA Headline', labelKey: 'admy.f_cta_headline', type: 'text', currentValue: '' },
    { key: 'team.cta_subtitle', label: 'CTA Subtitle', labelKey: 'admy.f_cta_subtitle3', type: 'textarea', currentValue: '' },
    { key: 'team.cta_button', label: 'CTA Button', labelKey: 'admy.f_cta_button2', type: 'text', currentValue: '' },
    { key: 'team.show_more', label: 'Mehr anzeigen', labelKey: 'admy.f_show_more', type: 'text', currentValue: '' },
    { key: 'team.show_less', label: 'Weniger anzeigen', labelKey: 'admy.f_show_less', type: 'text', currentValue: '' },
  ],
  gallery: [
    { key: 'gallery.tag', label: 'Tag', labelKey: 'admy.f_tag', type: 'text', currentValue: '' },
    { key: 'gallery.heading_line1', label: 'Headline Zeile 1', labelKey: 'admy.f_headline_line1', type: 'text', currentValue: '' },
    { key: 'gallery.heading_line2', label: 'Headline Zeile 2', labelKey: 'admy.f_headline_line2', type: 'text', currentValue: '' },
    { key: 'gallery.subtitle', label: 'Untertitel', labelKey: 'admy.f_subtitle', type: 'textarea', currentValue: '' },
    { key: 'gallery.filter_all', label: 'Filter — Alle', labelKey: 'admy.f_filter_all', type: 'text', currentValue: '' },
    { key: 'gallery.filter_dental', label: 'Filter — Dental', labelKey: 'admy.f_filter_dental', type: 'text', currentValue: '' },
    { key: 'gallery.filter_surgery', label: 'Filter — Chirurgie', labelKey: 'admy.f_filter_surgery', type: 'text', currentValue: '' },
    { key: 'gallery.filter_hair', label: 'Filter — Haar', labelKey: 'admy.f_filter_hair', type: 'text', currentValue: '' },
    { key: 'gallery.filter_aesthetic', label: 'Filter — Ästhetik', labelKey: 'admy.f_filter_aesthetic', type: 'text', currentValue: '' },
    { key: 'gallery.before_label', label: 'Vorher-Label', labelKey: 'admy.f_before_label', type: 'text', currentValue: '' },
    { key: 'gallery.after_label', label: 'Nachher-Label', labelKey: 'admy.f_after_label', type: 'text', currentValue: '' },
    { key: 'gallery.cta_heading', label: 'CTA — Überschrift', labelKey: 'admy.f_cta_heading', type: 'text', currentValue: '' },
    { key: 'gallery.cta_button', label: 'CTA — Button', labelKey: 'admy.f_cta_button', type: 'text', currentValue: '' },
    { key: 'images.gallery.hero_bg', label: 'Bild — Hero Hintergrund', labelKey: 'admy.f_img_hero_bg', type: 'image', currentValue: '' },
  ],
};

// Default image URLs
const DEFAULT_IMAGES: Record<string, string> = {
  'images.home.hero_bg': 'https://public.readdy.ai/ai/img_res/edited_e6d24e262cf8c44b5c8f89e2b9f1a942_aa491181.jpg',
  'images.home.services_hair_img': 'https://public.readdy.ai/ai/img_res/edited_32529c34cf27621dea8de32b0e08f3b6_6d353339.jpg',
  'images.home.services_dental_img': 'https://public.readdy.ai/ai/img_res/edited_a494e07768616535f81cc673682638f9_37caae9f.jpg',
  'images.home.services_botox_img': 'https://public.readdy.ai/ai/img_res/edited_1f7e46d18c7b93468418a6f9e805f52a_04fc9dc7.jpg',
  'images.home.about_img': 'https://public.readdy.ai/ai/img_res/37d99c2b-77e8-4c73-bb19-5697c037374a.png',
  'images.home.cta_bg': 'https://public.readdy.ai/ai/img_res/edited_1f7e46d18c7b93468418a6f9e805f52a_04fc9dc7.jpg',
  'images.home.process_img': 'https://readdy.ai/api/search-image?query=Modern%20aesthetic%20clinic%20interior%20with%20warm%20lighting%20and%20elegant%20design%20cream%20and%20beige%20tones%20professional%20medical%20atmosphere%20editorial%20photography&width=1400&height=800&seq=process-bg-1&orientation=landscape',
  'images.home.trust_img': 'https://readdy.ai/api/search-image?query=Modern%20dental%20clinic%20operating%20room%20with%20advanced%20equipment%20and%20warm%20lighting%20professional%20medical%20setting%20cream%20and%20beige%20tones%20editorial%20photography&width=1400&height=800&seq=trust-bg-1&orientation=landscape',
  'images.home.instagram_1': 'https://readdy.ai/api/search-image?query=Beautiful%20smile%20after%20dental%20treatment%20close%20up%20white%20teeth%20aesthetic%20dentistry%20result%20soft%20lighting%20professional%20photography&width=600&height=600&seq=insta-1&orientation=squarish',
  'images.home.instagram_2': 'https://readdy.ai/api/search-image?query=Luxury%20beauty%20clinic%20treatment%20room%20with%20warm%20ambient%20lighting%20cream%20interior%20elegant%20and%20professional%20atmosphere&width=600&height=600&seq=insta-2&orientation=squarish',
  'images.home.instagram_3': 'https://readdy.ai/api/search-image?query=Hair%20transplant%20before%20after%20result%20beautiful%20natural%20hairline%20professional%20medical%20photography%20soft%20lighting&width=600&height=600&seq=insta-3&orientation=squarish',
  'images.home.instagram_4': 'https://readdy.ai/api/search-image?query=Modern%20aesthetic%20clinic%20reception%20with%20flowers%20and%20warm%20lighting%20elegant%20cream%20interior%20design%20professional%20photography&width=600&height=600&seq=insta-4&orientation=squarish',
  'images.about.hero_bg': 'https://readdy.ai/api/search-image?query=Aerial%20drone%20view%20of%20downtown%20Tirana%20Albania%20cityscape%20Skanderbeg%20Square%20with%20national%20museum%20and%20historic%20clock%20tower%20colorful%20painted%20apartment%20buildings%20in%20bright%20yellow%20green%20red%20orange%20facades%20Dajti%20mountain%20looming%20behind%20city%20dramatic%20sunset%20with%20orange%20pink%20sky%20vibrant%20urban%20panorama%20wide%20angle%20lens%20photography&width=1920&height=1080&seq=tirana-aerial-sunset-2026&orientation=landscape',
  'images.about.doctor_img': 'https://public.readdy.ai/ai/img_res/37d99c2b-77e8-4c73-bb19-5697c037374a.png',
  'images.about.mission_img': 'https://readdy.ai/api/search-image?query=Doctor%20and%20patient%20consultation%20in%20modern%20clinic%20warm%20lighting%20professional%20atmosphere%20trust%20and%20care%20cream%20and%20beige%20interior%20editorial%20photography&width=800&height=600&seq=about-mission-1&orientation=landscape',
  'images.about.clinic_img': 'https://readdy.ai/api/search-image?query=Modern%20aesthetic%20clinic%20exterior%20building%20in%20Tirana%20Albania%20contemporary%20architecture%20glass%20facade%20warm%20evening%20light%20professional%20architectural%20photography&width=800&height=600&seq=about-clinic-1&orientation=landscape',
  'images.services.hero_bg': 'https://readdy.ai/api/search-image?query=Elegant%20beauty%20clinic%20reception%20area%20with%20warm%20lighting%20cream%20marble%20walls%20minimalist%20luxury%20design%20comfortable%20seating%20indoor%20plants%20soft%20ambient%20glow%20professional%20medical%20aesthetic%20atmosphere%20high-end%20interior%20design&width=1800&height=1040&seq=services-hero-1&orientation=landscape',
  'images.services.hair_img': 'https://readdy.ai/api/search-image?query=Hair%20transplant%20procedure%20in%20modern%20clinic%20professional%20medical%20setting%20warm%20lighting%20advanced%20equipment%20cream%20and%20beige%20tones%20editorial%20photography&width=800&height=600&seq=svc-hair-1&orientation=landscape',
  'images.services.dental_img': 'https://readdy.ai/api/search-image?query=Modern%20dental%20clinic%20treatment%20room%20with%20advanced%20equipment%20dentist%20chair%20warm%20lighting%20professional%20medical%20atmosphere%20cream%20and%20beige%20interior%20editorial%20photography&width=800&height=600&seq=svc-dental-1&orientation=landscape',
  'images.services.botox_img': 'https://readdy.ai/api/search-image?query=Aesthetic%20medicine%20treatment%20room%20with%20warm%20lighting%20professional%20medical%20equipment%20elegant%20cream%20interior%20minimalist%20design%20editorial%20photography&width=800&height=600&seq=svc-botox-1&orientation=landscape',
  'images.services.surgery_img': 'https://readdy.ai/api/search-image?query=Modern%20surgical%20suite%20with%20advanced%20medical%20equipment%20warm%20lighting%20professional%20operating%20room%20cream%20and%20beige%20tones%20editorial%20photography&width=800&height=600&seq=svc-surgery-1&orientation=landscape',
  'images.pricing.hero_bg': 'https://readdy.ai/api/search-image?query=Luxurious%20beauty%20clinic%20consultation%20room%20with%20warm%20lighting%20elegant%20minimalist%20interior%20cream%20and%20beige%20tones%20comfortable%20chairs%20professional%20yet%20welcoming%20atmosphere%20soft%20ambient%20glow%20high-end%20medical%20aesthetic%20clinic%20design&width=1800&height=920&seq=pricing-hero-1&orientation=landscape',
  'images.pricing.essential_img': 'https://readdy.ai/api/search-image?query=Minimalist%20beauty%20clinic%20treatment%20room%20simple%20elegant%20design%20warm%20lighting%20cream%20and%20white%20tones%20professional%20medical%20atmosphere%20editorial%20photography&width=600&height=400&seq=pkg-essential-1&orientation=landscape',
  'images.pricing.premium_img': 'https://readdy.ai/api/search-image?query=Premium%20beauty%20clinic%20VIP%20room%20with%20elegant%20furniture%20warm%20lighting%20cream%20and%20gold%20accents%20luxury%20medical%20atmosphere%20editorial%20photography&width=600&height=400&seq=pkg-premium-1&orientation=landscape',
  'images.pricing.luxe_img': 'https://readdy.ai/api/search-image?query=Luxury%20clinic%20suite%20with%20chandelier%20and%20elegant%20design%20warm%20ambient%20lighting%20cream%20and%20beige%20interior%20high-end%20medical%20facility%20editorial%20photography&width=600&height=400&seq=pkg-luxe-1&orientation=landscape',
  'images.pricing.exclusif_img': 'https://readdy.ai/api/search-image?query=Exclusive%20private%20clinic%20suite%20with%20panoramic%20view%20ultimate%20luxury%20design%20warm%20lighting%20cream%20and%20white%20interior%20editorial%20photography&width=600&height=400&seq=pkg-exclusif-1&orientation=landscape',
  'images.booking.hero_bg': 'https://public.readdy.ai/ai/img_res/edited_6ae96513ab7d589359d8b6e225297343_9ced89c2.jpg',
  'images.gallery.hero_bg': 'https://readdy.ai/api/search-image?query=Modern%20aesthetic%20clinic%20showcase%20room%20with%20before%20after%20displays%20warm%20lighting%20elegant%20cream%20interior%20professional%20medical%20photography%20gallery%20presentation&width=1800&height=920&seq=gallery-hero-1&orientation=landscape',
};

const PAGE_LABELS: Record<string, string> = {
  home: 'Startseite',
  about: 'Über uns',
  services: 'Leistungen',
  pricing: 'Preise',
  booking: 'Terminbuchung',
  team: 'Team',
  gallery: 'Galerie',
};

const PAGE_LABEL_KEYS: Record<string, string> = {
  home: 'adm2.pc_page_home',
  about: 'adm2.pc_page_about',
  services: 'adm2.pc_page_services',
  pricing: 'adm2.pc_page_pricing',
  booking: 'adm2.pc_page_booking',
  team: 'adm2.pc_page_team',
  gallery: 'adm2.pc_page_gallery',
};

const TEAM_STORAGE_KEY = 'esteva_admin_team_members';

export default function PageEditorPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [values, setValues] = useState<Record<string, string>>({});
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');
  const [editingLang, setEditingLang] = useState(i18n.language || 'de');
  const [uploadingField, setUploadingField] = useState<string | null>(null);
  const [uploadSuccessField, setUploadSuccessField] = useState<string | null>(null);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const activeUploadFieldRef = useRef<string | null>(null);

  const triggerUpload = (fieldKey: string) => {
    activeUploadFieldRef.current = fieldKey;
    setTimeout(() => {
      fileInputRef.current?.click();
    }, 0);
  };

  const fields = useMemo(() => PAGE_FIELDS[slug || ''] || [], [slug]);
  const pageLabel = PAGE_LABELS[slug || ''] || slug;
  const pageLabelDisplay = (slug && PAGE_LABEL_KEYS[slug]) ? t(PAGE_LABEL_KEYS[slug]) : pageLabel;
  const isTeamPage = slug === 'team';
  const isPricingPage = slug === 'pricing';

  // Load team members from localStorage or fallback to mock
  const getInitialTeamMembers = (): TeamMember[] => {
    const stored = localStorage.getItem(TEAM_STORAGE_KEY);
    if (stored) {
      try {
        return JSON.parse(stored);
      } catch {
        return teamMembers;
      }
    }
    return teamMembers;
  };

  // Load values when slug or language changes
  useEffect(() => {
    if (sessionStorage.getItem('admin_logged_in') !== 'true') {
      navigate('/admin');
      return;
    }

    const STORAGE_KEY = `esteva_admin_content_${slug}_${editingLang}`;
    const stored = localStorage.getItem(STORAGE_KEY);
    const initialValues: Record<string, string> = {};

    fields.forEach((field) => {
      if (field.type === 'image') {
        initialValues[field.key] = stored
          ? (JSON.parse(stored)[field.key] || DEFAULT_IMAGES[field.key] || '')
          : (DEFAULT_IMAGES[field.key] || '');
      } else {
        const resourceBundle = i18n.getResourceBundle(editingLang, 'common');
        const translationValue = resourceBundle?.[field.key] || t(field.key);
        initialValues[field.key] = stored ? (JSON.parse(stored)[field.key] || translationValue) : translationValue;
      }
    });

    setValues(initialValues);
    setSaved(false);
    setError('');
  }, [slug, editingLang, navigate, t, i18n, fields]);

  const handleChange = (key: string, value: string) => {
    setValues((prev) => ({ ...prev, [key]: value }));
    setSaved(false);
  };

  const handleSave = () => {
    if (!slug) return;
    const STORAGE_KEY = `esteva_admin_content_${slug}_${editingLang}`;
    localStorage.setItem(STORAGE_KEY, JSON.stringify(values));

    // Wenn Deutsch: automatisch in alle anderen Sprachen syncen
    if (editingLang === 'de') {
      const ALL_LANGS = ['en', 'it', 'es', 'fr'];
      ALL_LANGS.forEach((lang) => {
        const langKey = `esteva_admin_content_${slug}_${lang}`;
        localStorage.setItem(langKey, JSON.stringify(values));
      });
    }

    setSaved(true);
    setError('');
    window.dispatchEvent(new Event('admin-content-updated'));
    // Activity logging
    const logKey = 'esteva_admin_activity_log';
    const existing = localStorage.getItem(logKey);
    const log = existing ? JSON.parse(existing) : [];
    log.unshift({
      action: 'page_edited',
      page: slug,
      pageLabel: pageLabel,
      lang: editingLang === 'de' ? 'de (+ alle Sprachen)' : editingLang,
      timestamp: new Date().toISOString(),
    });
    if (log.length > 20) log.pop();
    localStorage.setItem(logKey, JSON.stringify(log));
    window.dispatchEvent(new Event('admin-activity-updated'));
    setTimeout(() => setSaved(false), 3000);
  };

  const handleReset = () => {
    if (!slug) return;
    const STORAGE_KEY = `esteva_admin_content_${slug}_${editingLang}`;
    localStorage.removeItem(STORAGE_KEY);

    const resetValues: Record<string, string> = {};
    fields.forEach((field) => {
      if (field.type === 'image') {
        resetValues[field.key] = DEFAULT_IMAGES[field.key] || '';
      } else {
        const resourceBundle = i18n.getResourceBundle(editingLang, 'common');
        resetValues[field.key] = resourceBundle?.[field.key] || t(field.key);
      }
    });
    setValues(resetValues);
    setError(t('adm2.changes_reset_for', { lang: editingLang.toUpperCase() }));
    setTimeout(() => setError(''), 3000);
  };

  const handleImageUpload = (fieldKey: string, file: File) => {
    if (!file) return;

    const validImageTypes = ['image/jpeg', 'image/png', 'image/webp'];
    if (!validImageTypes.includes(file.type)) {
      setError(t('adm2.only_jpg_png_webp'));
      setTimeout(() => setError(''), 3000);
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      setError(t('adm2.file_too_large_max'));
      setTimeout(() => setError(''), 3000);
      return;
    }

    setUploadingField(fieldKey);
    const reader = new FileReader();
    reader.onload = (e) => {
      const base64 = e.target?.result as string;
      setValues((prev) => ({ ...prev, [fieldKey]: base64 }));
      setSaved(false);
      setUploadingField(null);
      setUploadSuccessField(fieldKey);
      setTimeout(() => setUploadSuccessField(null), 2000);
    };
    reader.onerror = () => {
      setUploadingField(null);
      setError(t('adm2.file_read_error'));
      setTimeout(() => setError(''), 3000);
    };
    reader.readAsDataURL(file);
  };

  const getOriginalValue = (field: EditableField): string => {
    if (field.type === 'image') {
      return DEFAULT_IMAGES[field.key] || '';
    }
    const resourceBundle = i18n.getResourceBundle(editingLang, 'common');
    return resourceBundle?.[field.key] || '';
  };

  const isChanged = (field: EditableField): boolean => {
    return values[field.key] !== getOriginalValue(field);
  };

  const handleTeamSave = (members: TeamMember[]) => {
    localStorage.setItem(TEAM_STORAGE_KEY, JSON.stringify(members));
    window.dispatchEvent(new Event('admin-content-updated'));
    // Activity logging
    const logKey = 'esteva_admin_activity_log';
    const existing = localStorage.getItem(logKey);
    const log = existing ? JSON.parse(existing) : [];
    log.unshift({
      action: 'team_updated',
      page: 'team',
      pageLabel: 'Team',
      count: members.length,
      timestamp: new Date().toISOString(),
    });
    if (log.length > 20) log.pop();
    localStorage.setItem(logKey, JSON.stringify(log));
    window.dispatchEvent(new Event('admin-activity-updated'));
  };

  if (sessionStorage.getItem('admin_logged_in') !== 'true') return null;

  return (
    <div className="min-h-screen bg-background-100">
      <header className="bg-background-50 border-b border-background-200/70 sticky top-0 z-30">
        <div className="max-w-4xl mx-auto px-4 md:px-6 min-h-16 py-3 md:py-0 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex items-center gap-4 min-w-0">
            <Link
              to="/admin/dashboard"
              className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-background-100 transition-colors cursor-pointer no-underline shrink-0"
            >
              <i className="ri-arrow-left-line text-foreground-600"></i>
            </Link>
            <div className="min-w-0">
              <h1 className="font-label text-sm font-semibold text-foreground-900">
                {t('adm2.edit_page_title', { page: pageLabelDisplay })}
              </h1>
              <p className="text-[10px] text-foreground-500 font-body">
                {isTeamPage ? t('adm2.manage_team_members') : isPricingPage ? t('adm2.edit_pricing_treatments') : t('adm2.editable_fields_count2', { count: fields.length })}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {!isTeamPage && !isPricingPage && (
              <>
                <button
                  onClick={handleReset}
                  className="flex-1 sm:flex-none px-4 py-1.5 rounded-lg border border-background-200 text-xs text-foreground-600 font-label hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
                >
                  {t('adm2.reset')}
                </button>
                <button
                  onClick={handleSave}
                  className="flex-1 sm:flex-none px-5 py-1.5 rounded-lg bg-primary-500 text-white text-xs font-label hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                >
                  {t('adm2.save')}
                </button>
              </>
            )}
          </div>
        </div>
        {/* Language Tabs */}
        {!isTeamPage && !isPricingPage && (
          <div className="border-t border-background-200/70 px-4 md:px-6 py-2">
            <div className="max-w-4xl mx-auto flex items-center gap-1 flex-wrap">
              {SUPPORTED_LANGS.map((lang) => (
                <button
                  key={lang.code}
                  onClick={() => setEditingLang(lang.code)}
                  className={`px-4 py-1.5 rounded-full text-xs font-label tracking-[0.06em] transition-all cursor-pointer whitespace-nowrap ${
                    editingLang === lang.code
                      ? 'bg-primary-500 text-white'
                      : 'text-foreground-500 hover:text-foreground-700 hover:bg-background-100'
                  }`}
                >
                  {lang.flag} {lang.label}
                </button>
              ))}
            </div>
          </div>
        )}
        {saved && (
          <div className="bg-green-50 border-t border-green-200 text-center py-2">
            <span className="text-xs text-green-700 font-body">
              {editingLang === 'de'
                ? t('adm2.saved_de_synced')
                : t('adm2.saved_lang_live', { lang: editingLang.toUpperCase() })}
            </span>
          </div>
        )}
        {error && (
          <div className="bg-amber-50 border-t border-amber-200 text-center py-2">
            <span className="text-xs text-amber-700 font-body">{error}</span>
          </div>
        )}
      </header>

      <main className="max-w-4xl mx-auto px-4 md:px-6 py-8">
        {!isTeamPage && (
          <div className="bg-accent-50 border border-accent-200/60 rounded-xl p-4 mb-6">
            <div className="flex items-start gap-3">
              <i className="ri-information-line text-accent-600 mt-0.5"></i>
              <div>
                <p className="text-xs text-accent-800 font-label font-semibold">{t('adm2.lang_editor', { lang: editingLang.toUpperCase() })}</p>
                <p className="text-xs text-accent-700 font-body mt-0.5">
                  {editingLang === 'de'
                    ? t('adm2.lang_editor_de_note')
                    : t('adm2.lang_editor_other_note', { lang: SUPPORTED_LANGS.find((l) => l.code === editingLang)?.label })}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Team Member Editor */}
        {isTeamPage && (
          <TeamMemberEditor
            initialMembers={getInitialTeamMembers()}
            onSave={handleTeamSave}
          />
        )}

        {/* Pricing Editor */}
        {isPricingPage && (
          <PricingEditor onSave={() => {
            window.dispatchEvent(new Event('admin-content-updated'));
          }} />
        )}

        {/* Image Fields Section */}
        {!isTeamPage && !isPricingPage && fields.some((f) => f.type === 'image') && (
          <div className="mb-10">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-6 h-6 flex items-center justify-center rounded-md bg-accent-100">
                <i className="ri-image-line text-xs text-accent-600"></i>
              </div>
              <h2 className="font-label text-xs uppercase tracking-[0.15em] text-foreground-500">{t('adm2.images')}</h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {fields.filter((f) => f.type === 'image').map((field) => (
                <div key={field.key} className="bg-background-50 rounded-xl border border-background-200/70 p-4">
                  <label className="block text-xs tracking-[0.08em] uppercase font-label text-foreground-500 mb-2">
                    {t(field.labelKey)}
                  </label>
                  <div className="relative w-full aspect-video rounded-lg overflow-hidden bg-background-200 mb-3 border border-background-200/40">
                    {values[field.key] ? (
                      <>
                        <img
                          src={values[field.key]}
                          alt={t(field.labelKey)}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            (e.target as HTMLImageElement).style.display = 'none';
                            (e.target as HTMLImageElement).nextElementSibling?.classList.remove('hidden');
                          }}
                        />
                        {uploadSuccessField === field.key && (
                          <div className="absolute top-2 right-2 bg-green-500 text-white text-[10px] font-label px-2 py-0.5 rounded-full flex items-center gap-1 animate-pulse">
                            <i className="ri-check-line"></i> {t('adm2.uploaded')}
                          </div>
                        )}
                      </>
                    ) : null}
                    <div className={`w-full h-full flex items-center justify-center ${values[field.key] ? 'hidden' : ''}`}>
                      <div className="text-center">
                        <i className="ri-image-add-line text-2xl text-foreground-300"></i>
                        <p className="mt-1 text-[10px] text-foreground-400 font-body">{t('adm2.no_image')}</p>
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-2 mb-2">
                    <button
                      type="button"
                      onClick={() => triggerUpload(field.key)}
                      className={`flex-1 px-3 py-2 rounded-lg border border-dashed border-background-300 bg-background-50 text-[11px] text-foreground-600 font-label hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap flex items-center justify-center gap-1.5 ${
                        uploadingField === field.key ? 'opacity-60 pointer-events-none' : ''
                      }`}
                    >
                      {uploadingField === field.key ? (
                        <>
                          <div className="w-3 h-3 border-2 border-foreground-400 border-t-transparent rounded-full animate-spin"></div>
                          {t('adm2.uploading_progress')}
                        </>
                      ) : (
                        <>
                          <i className="ri-upload-cloud-2-line text-xs"></i>
                          {t('adm2.upload_image')}
                        </>
                      )}
                    </button>
                    {values[field.key] && (
                      <button
                        type="button"
                        onClick={() => handleChange(field.key, '')}
                        className="px-3 py-2 rounded-lg border border-red-200 bg-red-50 text-[11px] text-red-600 font-label hover:bg-red-100 transition-colors cursor-pointer whitespace-nowrap"
                        title={t('adm2.remove_image')}
                      >
                        <i className="ri-delete-bin-line"></i>
                      </button>
                    )}
                  </div>
                  <input
                    type="file"
                    ref={fileInputRef}
                    accept="image/jpeg,image/png,image/webp"
                    style={{ display: 'none' }}
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      const fieldKey = activeUploadFieldRef.current;
                      if (file && fieldKey) {
                        handleImageUpload(fieldKey, file);
                      }
                      if (fileInputRef.current) {
                        fileInputRef.current.value = '';
                      }
                      activeUploadFieldRef.current = null;
                    }}
                  />
                  <input
                    type="text"
                    value={values[field.key] || ''}
                    onChange={(e) => handleChange(field.key, e.target.value)}
                    className="w-full px-3 py-2 rounded-lg border border-background-200 bg-background-50 text-[11px] text-foreground-900 font-mono focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-100 transition-all"
                    placeholder={t('adm2.enter_image_url')}
                  />
                  <p className="mt-1.5 text-[9px] text-foreground-400 font-body">
                    {t('adm2.image_upload_hint')}
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Text Fields Section */}
        {!isTeamPage && !isPricingPage && fields.some((f) => f.type !== 'image') && (
          <div>
            <div className="flex items-center gap-2 mb-4">
              <div className="w-6 h-6 flex items-center justify-center rounded-md bg-primary-100">
                <i className="ri-file-text-line text-xs text-primary-600"></i>
              </div>
              <h2 className="font-label text-xs uppercase tracking-[0.15em] text-foreground-500">
                {t('adm2.texts')} ({SUPPORTED_LANGS.find((l) => l.code === editingLang)?.label})
              </h2>
            </div>
            <div className="space-y-4">
              {fields.filter((f) => f.type !== 'image').map((field) => (
                <div key={field.key} className="bg-background-50 rounded-xl border border-background-200/70 p-4">
                  <label className="block text-xs tracking-[0.08em] uppercase font-label text-foreground-500 mb-1">
                    {t(field.labelKey)}
                  </label>
                  <p className="text-[10px] text-foreground-400 font-body mb-3">{t('adm2.key_label')} {field.key}</p>
                  {field.type === 'textarea' ? (
                    <textarea
                      value={values[field.key] || ''}
                      onChange={(e) => handleChange(field.key, e.target.value)}
                      className="w-full px-4 py-3 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-900 font-body leading-relaxed focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all resize-y min-h-[80px]"
                      rows={3}
                    />
                  ) : (
                    <input
                      type="text"
                      value={values[field.key] || ''}
                      onChange={(e) => handleChange(field.key, e.target.value)}
                      className="w-full px-4 py-3 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-900 font-body focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all"
                    />
                  )}
                  {isChanged(field) && (
                    <p className="mt-2 text-[10px] text-primary-600 font-body flex items-center gap-1">
                      <i className="ri-pencil-line"></i>
                      {t('adm2.changed_original')} <span className="text-foreground-400 italic line-clamp-1">{getOriginalValue(field)}</span>
                    </p>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {!isTeamPage && !isPricingPage && fields.length === 0 && (
          <div className="text-center py-20">
            <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-200/60">
              <i className="ri-file-edit-line text-2xl text-foreground-400"></i>
            </div>
            <h2 className="mt-4 font-heading text-lg text-foreground-600">{t('adm2.no_editable_fields')}</h2>
            <p className="mt-1 text-sm text-foreground-500 font-body">{t('adm2.no_configured_fields')}</p>
          </div>
        )}
      </main>
    </div>
  );
}